import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/ApiService';
import Footer from '../Footer';
import PageLoader from '../ui/PageLoader';
import HomeHero from './HomeHero';
import GarageSnapshot from './GarageSnapshot';
import NextBestActions from './NextBestActions';
import RoleAttentionModule from './RoleAttentionModule';
import FeaturedBuildCard from './FeaturedBuildCard';
import HomeSection from './HomeSection';
import UpcomingHomeEvents from './UpcomingHomeEvents';
import BuildRail from './BuildRail';
import ProductUpdates from './ProductUpdates';
import SponsoredPlacement from './SponsoredPlacement';
import { OrganizerConversion, ClubConversion } from './OrganizerConversion';
import HomeCommunityFeed from './HomeCommunityFeed';
import HomeMyClubs from './HomeMyClubs';
import {
  normalizeSponsorship,
  type AdCreative,
  type AuthenticatedHomeData,
  type HomeRecognitionSummary,
} from './homeTypes';
import ContributorRequestsPanel from '../contributors/ContributorRequestsPanel';
import { useOnboarding } from '../../hooks/useOnboarding';
import {
  OnboardingResumeCard,
  OnboardingWelcomeCard,
  SetupChecklistCard,
} from '../onboarding/OnboardingCards';
import {
  buildBuilderChecklist,
  checklistProgress,
  shouldShowBuilderChecklist,
  shouldShowOnboardingResume,
  shouldShowWelcome,
  type ChecklistStep,
} from '../../utils/onboarding';
import { vehicleProfilePath, resolveVehicleNavSlug } from '../../utils/vehicleUrls';

function recognitionSummary(
  value: AuthenticatedHomeData['recognition']
): HomeRecognitionSummary | null {
  if (!value || Array.isArray(value)) return null;
  const awards = Number(value.award_count || 0);
  const badges = Number(value.badge_count || 0);
  if (awards <= 0 && badges <= 0) return null;
  return { award_count: awards, badge_count: badges };
}

/**
 * Personal landing page for signed-in builders.
 * Prioritizes garage work, upcoming events, club/event updates, and role tasks.
 * Deliberately omits sitewide discovery rails (recently updated / recognized).
 */
export const AuthenticatedHomePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isOrganizer } = useAuth();
  const onboarding = useOnboarding();
  const [data, setData] = useState<AuthenticatedHomeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [sectionError, setSectionError] = useState(false);
  const [sponsorship, setSponsorship] = useState<AdCreative | null>(null);

  const loadHome = useCallback(async () => {
    setLoading(true);
    setSectionError(false);
    try {
      const [home, ad] = await Promise.all([
        apiService.getHomeAuthenticated(),
        apiService.getAdPlacement('home_authenticated').catch(() => null),
      ]);
      setData(home);
      setSponsorship(normalizeSponsorship(home.sponsorship) || ad);
      void apiService.trackEvent('home_viewed', { metadata: { audience: 'authenticated' } });
    } catch {
      setData({});
      setSectionError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadHome();
  }, [loadHome]);

  const greeting =
    data?.greeting ||
    (user?.first_name
      ? `Welcome back, ${user.first_name}`
      : user?.username
        ? `Welcome back, ${user.username}`
        : 'Welcome back');

  const garageVehicles = useMemo(() => data?.garage?.vehicles || [], [data?.garage?.vehicles]);
  const garageEmpty = garageVehicles.length === 0;
  const favoriteBuilds = data?.favorite_builds || [];
  const communityFeed = data?.community_feed || [];
  const myClubs = data?.my_clubs || [];
  const upcoming = data?.upcoming_events || [];
  const recognition = recognitionSummary(data?.recognition);
  const showOrganizerConvert = !isOrganizer;
  const showClubConvert = myClubs.length === 0;

  const checklistSteps = useMemo(() => {
    if (!user?.username) return [] as ChecklistStep[];
    const snapshots = garageVehicles.map((vehicle) => {
      const completeness = (vehicle as { completeness?: { percent?: number } }).completeness;
      const slug = resolveVehicleNavSlug(vehicle);
      return {
        id: vehicle.id,
        slugPath: slug ? vehicleProfilePath(user.username, slug) : '/garage',
        engine: vehicle.engine,
        transmission: null,
        drivetrain: vehicle.drivetrain,
        chassis: vehicle.chassis,
        description: vehicle.description,
        imageCount: vehicle.image_url ? 1 : 0,
        modificationCount: vehicle.modification_count ?? 0,
        completenessPercent: completeness?.percent ?? 0,
        isPublic: vehicle.is_public,
      };
    });
    return buildBuilderChecklist(snapshots, onboarding.state, user.username);
  }, [garageVehicles, onboarding.state, user?.username]);

  const showWelcome = shouldShowWelcome(onboarding.state, garageVehicles.length);
  const showChecklist = shouldShowBuilderChecklist(onboarding.state, checklistSteps);
  const showResume = shouldShowOnboardingResume(onboarding.state, checklistSteps);

  const { loading: onboardingLoading, state: onboardingState, markStarted, markCompleted } =
    onboarding;

  useEffect(() => {
    if (!onboardingLoading && (showWelcome || showChecklist) && !onboardingState.started_at) {
      void markStarted();
    }
  }, [
    onboardingLoading,
    onboardingState.started_at,
    markStarted,
    showWelcome,
    showChecklist,
  ]);

  useEffect(() => {
    const { done, total } = checklistProgress(checklistSteps);
    if (total > 0 && done === total && !onboardingState.completed_at) {
      void markCompleted();
    }
  }, [checklistSteps, onboardingState.completed_at, markCompleted]);

  const handleChecklistAction = (step: ChecklistStep) => {
    if (step.actionId === 'add_vehicle') {
      navigate('/garage', { state: { openAddVehicle: true } });
      return;
    }
    if (step.href) {
      navigate(step.href);
    }
  };

  if (loading) {
    return <PageLoader message="Loading your home…" />;
  }

  return (
    <div className="page relative overflow-x-hidden">
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(249,115,22,0.07),transparent_55%)]"
        aria-hidden
      />
      <div className="film-grain opacity-[0.035]" aria-hidden />

      <div className="relative z-[1]">
        <HomeHero
          variant="authenticated"
          greeting={greeting}
          subtitle={
            garageEmpty
              ? 'Start with a vehicle, or explore builds first.'
              : 'Your garage, events, and club updates in one place.'
          }
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {showWelcome ? (
            <OnboardingWelcomeCard
              onAddVehicle={() => navigate('/garage', { state: { openAddVehicle: true } })}
              onExplore={() => {
                void onboarding.dismissWelcome();
                navigate('/discover');
              }}
              onSkip={() => void onboarding.dismissWelcome()}
            />
          ) : null}

          {showChecklist && !showWelcome ? (
            <SetupChecklistCard
              steps={checklistSteps}
              onDismiss={() => void onboarding.dismissChecklist()}
              onStepAction={handleChecklistAction}
            />
          ) : null}

          {showResume && !showWelcome && !showChecklist ? (
            <OnboardingResumeCard onContinue={() => void onboarding.showSetupGuide()} />
          ) : null}
        </div>

        {sectionError ? (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4" role="status">
            <p className="text-gray-500 text-sm mb-3">
              Some home modules couldn&apos;t load. Your garage is still available.
            </p>
            <Button type="button" variant="secondary" className="min-h-[44px]" onClick={() => void loadHome()}>
              Try again
            </Button>
          </div>
        ) : null}

        <GarageSnapshot garage={data?.garage} nextActions={data?.next_actions} />
        <NextBestActions actions={data?.next_actions || []} />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <ContributorRequestsPanel />
        </div>

        {(data?.role_alerts?.length ?? 0) > 0 ? (
          <RoleAttentionModule alerts={data!.role_alerts!} />
        ) : null}

        {upcoming.length > 0 ? <UpcomingHomeEvents events={upcoming} /> : null}

        {communityFeed.length > 0 ? <HomeCommunityFeed items={communityFeed} /> : null}

        {myClubs.length > 0 ? <HomeMyClubs clubs={myClubs} /> : null}

        {recognition ? (
          <HomeSection title="Your recognition" description="Awards and badges across your builds.">
            <div className="grid grid-cols-2 gap-3 max-w-md">
              <div className="rounded-2xl border border-white/[0.06] bg-[#0c0c0c]/70 px-4 py-4">
                <p className="stat-numeral text-2xl">{recognition.award_count || 0}</p>
                <p className="spec-label mt-1">Awards</p>
              </div>
              <div className="rounded-2xl border border-white/[0.06] bg-[#0c0c0c]/70 px-4 py-4">
                <p className="stat-numeral text-2xl">{recognition.badge_count || 0}</p>
                <p className="spec-label mt-1">Badges</p>
              </div>
            </div>
          </HomeSection>
        ) : null}

        {favoriteBuilds.length > 0 ? (
          <BuildRail
            eyebrow="Saved"
            title="Favorite builds"
            description="Builds you have saved to revisit."
            vehicles={favoriteBuilds}
            viewAllHref="/favorites"
            layout="horizontal"
            max={6}
          />
        ) : null}

        {sponsorship ? <SponsoredPlacement creative={sponsorship} /> : null}

        {garageEmpty && data?.featured_build ? (
          <HomeSection
            title="Featured build"
            description="While you add your first build, here is a story worth browsing."
            className="!py-8"
          >
            <FeaturedBuildCard featured={data.featured_build} compact />
          </HomeSection>
        ) : null}

        {(data?.product_updates?.length ?? 0) > 0 ? (
          <ProductUpdates updates={data!.product_updates!} />
        ) : null}

        {showOrganizerConvert ? <OrganizerConversion /> : null}
        {showClubConvert ? <ClubConversion /> : null}

        <Footer />
      </div>
    </div>
  );
};

export default AuthenticatedHomePage;
